<script setup lang="ts">
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { FAVORITE_COUNTRIES, ALL_COUNTRIES } from '@/types/countries'
import type { Organisation } from '@/types/auth'
import type { OrganisationWriteError, UpdateOrganisationPayload } from '@/types/organisation-settings'

const { t } = useI18n()

const props = defineProps<{
  organisation: Organisation
  loading: boolean
  formErrors: Record<string, string[]>
  saved: boolean
  settingsError: OrganisationWriteError | null
  // Passed via shared RouterView slot — unused by this panel
  orgId?: number
  logoErrors?: string[]
  logoActionError?: OrganisationWriteError | null
  isOwner?: boolean
}>()

const emit = defineEmits<{
  save: [payload: UpdateOrganisationPayload]
  cancel: []
  // Declared to satisfy the shared RouterView slot's event listeners
  uploadLogo: [file: File]
  deleteLogo: []
}>()

const isEditing = ref<boolean>(false)

// Form fields
const addressLine1 = ref<string>('')
const addressLine2 = ref<string>('')
const city = ref<string>('')
const postalCode = ref<string>('')
const countryCode = ref<string>('')
const vatNumber = ref<string>('')
const orgEmail = ref<string>('')
const orgPhone = ref<string>('')
const website = ref<string>('')

const allCountries = [...FAVORITE_COUNTRIES, ...ALL_COUNTRIES]

function countryName(code: string | null | undefined): string {
  if (!code) return '—'
  return allCountries.find((c) => c.code === code)?.name ?? code
}

function populateForm(): void {
  addressLine1.value = props.organisation.address_line_1
  addressLine2.value = props.organisation.address_line_2 ?? ''
  city.value = props.organisation.city
  postalCode.value = props.organisation.postal_code
  countryCode.value = props.organisation.country_code
  vatNumber.value = props.organisation.vat_number ?? ''
  orgEmail.value = props.organisation.email ?? ''
  orgPhone.value = props.organisation.phone ?? ''
  website.value = props.organisation.website ?? ''
}

function startEditing(): void {
  populateForm()
  isEditing.value = true
}

function cancelEditing(): void {
  isEditing.value = false
  emit('cancel')
}

const isOwner = computed<boolean>(() => props.organisation.role === 'owner')

function handleSave(): void {
  const payload: UpdateOrganisationPayload = {
    address_line_1: addressLine1.value,
    address_line_2: addressLine2.value || null,
    city: city.value,
    postal_code: postalCode.value,
    country_code: countryCode.value,
    vat_number: vatNumber.value || null,
    email: orgEmail.value || null,
    phone: orgPhone.value || null,
    website: website.value || null,
  }
  emit('save', payload)
}

// Watch for saved prop to close editing mode
import { watch } from 'vue'
watch(
  () => props.saved,
  (val) => {
    if (val) {
      isEditing.value = false
    }
  },
)
</script>

<template>
  <div class="rounded-2xl border border-slate-700 bg-slate-800/50 p-6">
    <div class="flex items-center justify-between mb-4">
      <h2 class="text-sm font-semibold text-slate-300 uppercase tracking-wide">
        {{ t('organizer.organisation.title') }}
      </h2>
      <button
        v-if="!isEditing && isOwner"
        type="button"
        class="text-sm font-semibold text-emerald-400 hover:text-emerald-300 transition-colors"
        @click="startEditing"
      >
        {{ t('organizer.organisation.edit') }}
      </button>
    </div>

    <!-- Success message -->
    <div
      v-if="saved && !isEditing"
      class="mb-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30 px-4 py-2 text-sm text-emerald-400"
    >
      {{ t('organizer.organisation.saved') }}
    </div>

    <!-- Read-only view -->
    <template v-if="!isEditing">
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div class="sm:col-span-2">
          <p class="text-xs text-slate-500 uppercase tracking-wide font-medium">{{ t('organizer.organisation.name') }}</p>
          <p class="text-sm font-semibold text-white mt-0.5">{{ organisation.name }}</p>
        </div>
        <div class="sm:col-span-2">
          <p class="text-xs text-slate-500 uppercase tracking-wide font-medium">{{ t('organizer.organisation.url') }}</p>
          <p class="text-sm font-semibold text-white mt-0.5 font-mono">{{ organisation.slug }}</p>
          <p class="text-xs text-slate-500 italic mt-1">{{ t('organizer.organisation.name_slug_locked') }}</p>
        </div>
        <div class="sm:col-span-2">
          <p class="text-xs text-slate-500 uppercase tracking-wide font-medium">{{ t('organizer.organisation.address_line_1') }}</p>
          <p class="text-sm font-semibold text-white mt-0.5">{{ organisation.address_line_1 }}</p>
        </div>
        <div v-if="organisation.address_line_2" class="sm:col-span-2">
          <p class="text-xs text-slate-500 uppercase tracking-wide font-medium">{{ t('organizer.organisation.address_line_2') }}</p>
          <p class="text-sm font-semibold text-white mt-0.5">{{ organisation.address_line_2 }}</p>
        </div>
        <div>
          <p class="text-xs text-slate-500 uppercase tracking-wide font-medium">{{ t('organizer.organisation.city') }}</p>
          <p class="text-sm font-semibold text-white mt-0.5">{{ organisation.city }}</p>
        </div>
        <div>
          <p class="text-xs text-slate-500 uppercase tracking-wide font-medium">{{ t('organizer.organisation.postal_code') }}</p>
          <p class="text-sm font-semibold text-white mt-0.5">{{ organisation.postal_code }}</p>
        </div>
        <div>
          <p class="text-xs text-slate-500 uppercase tracking-wide font-medium">{{ t('organizer.organisation.country') }}</p>
          <p class="text-sm font-semibold text-white mt-0.5">{{ countryName(organisation.country_code) }}</p>
        </div>
        <div>
          <p class="text-xs text-slate-500 uppercase tracking-wide font-medium">{{ t('organizer.organisation.vat_number') }}</p>
          <p class="text-sm font-semibold text-white mt-0.5">{{ organisation.vat_number ?? '—' }}</p>
        </div>
        <div>
          <p class="text-xs text-slate-500 uppercase tracking-wide font-medium">{{ t('organizer.organisation.email') }}</p>
          <p class="text-sm font-semibold text-white mt-0.5">{{ organisation.email ?? '—' }}</p>
        </div>
        <div>
          <p class="text-xs text-slate-500 uppercase tracking-wide font-medium">{{ t('organizer.organisation.phone') }}</p>
          <p class="text-sm font-semibold text-white mt-0.5">{{ organisation.phone ?? '—' }}</p>
        </div>
        <div class="sm:col-span-2">
          <p class="text-xs text-slate-500 uppercase tracking-wide font-medium">{{ t('organizer.organisation.website') }}</p>
          <p class="text-sm font-semibold text-white mt-0.5">{{ organisation.website ?? '—' }}</p>
        </div>
      </div>
    </template>

    <!-- Edit form (owner only) -->
    <form v-else novalidate @submit.prevent="handleSave">
      <!-- Non-field write error: 403 (unverified email / not the owner / forbidden) or a
           genuinely transient 500. A 403 here is never transient — retrying cannot help. -->
      <div
        v-if="settingsError"
        class="mb-4 rounded-lg bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm text-red-400"
      >
        {{ t(settingsError.key) }}
        <RouterLink
          v-if="settingsError.kind === 'email_unverified'"
          :to="{ name: 'organizer.verify-email' }"
          class="ml-1 font-semibold underline hover:text-red-300"
        >
          {{ t('organizer.organisation.errors.email_unverified_link') }}
        </RouterLink>
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <!-- Name (read-only — locked after creation) -->
        <div class="sm:col-span-2">
          <p class="block text-xs font-medium text-slate-400 mb-1">{{ t('organizer.organisation.name') }}</p>
          <p class="text-sm text-white">{{ organisation.name }}</p>
          <p class="text-xs text-slate-500 italic mt-1">{{ t('organizer.organisation.name_slug_locked') }}</p>
        </div>

        <!-- Address line 1 -->
        <div class="sm:col-span-2">
          <label for="address_line_1" class="block text-xs font-medium text-slate-400 mb-1">
            {{ t('organizer.organisation.address_line_1') }}
          </label>
          <input
            id="address_line_1"
            v-model="addressLine1"
            type="text"
            autocomplete="address-line1"
            class="block w-full rounded-lg border px-3 py-2 text-sm text-white bg-slate-900 focus:outline-none focus:ring-1 transition-colors"
            :class="formErrors['address_line_1'] ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-slate-600 focus:border-emerald-500 focus:ring-emerald-500'"
          />
          <p v-if="formErrors['address_line_1']" class="mt-1 text-xs text-red-400">
            {{ formErrors['address_line_1'][0] }}
          </p>
        </div>

        <!-- Address line 2 -->
        <div class="sm:col-span-2">
          <label for="address_line_2" class="block text-xs font-medium text-slate-400 mb-1">
            {{ t('organizer.organisation.address_line_2') }}
            <span class="text-slate-600 font-normal ml-1">({{ t('common.optional') }})</span>
          </label>
          <input
            id="address_line_2"
            v-model="addressLine2"
            type="text"
            autocomplete="address-line2"
            class="block w-full rounded-lg border border-slate-600 px-3 py-2 text-sm text-white bg-slate-900 focus:outline-none focus:ring-1 focus:border-emerald-500 focus:ring-emerald-500 transition-colors"
          />
        </div>

        <!-- City -->
        <div>
          <label for="city" class="block text-xs font-medium text-slate-400 mb-1">
            {{ t('organizer.organisation.city') }}
          </label>
          <input
            id="city"
            v-model="city"
            type="text"
            autocomplete="address-level2"
            class="block w-full rounded-lg border px-3 py-2 text-sm text-white bg-slate-900 focus:outline-none focus:ring-1 transition-colors"
            :class="formErrors['city'] ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-slate-600 focus:border-emerald-500 focus:ring-emerald-500'"
          />
          <p v-if="formErrors['city']" class="mt-1 text-xs text-red-400">
            {{ formErrors['city'][0] }}
          </p>
        </div>

        <!-- Postal code -->
        <div>
          <label for="postal_code" class="block text-xs font-medium text-slate-400 mb-1">
            {{ t('organizer.organisation.postal_code') }}
          </label>
          <input
            id="postal_code"
            v-model="postalCode"
            type="text"
            autocomplete="postal-code"
            class="block w-full rounded-lg border px-3 py-2 text-sm text-white bg-slate-900 focus:outline-none focus:ring-1 transition-colors"
            :class="formErrors['postal_code'] ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-slate-600 focus:border-emerald-500 focus:ring-emerald-500'"
          />
          <p v-if="formErrors['postal_code']" class="mt-1 text-xs text-red-400">
            {{ formErrors['postal_code'][0] }}
          </p>
        </div>

        <!-- Country -->
        <div>
          <label for="country_code" class="block text-xs font-medium text-slate-400 mb-1">
            {{ t('organizer.organisation.country') }}
          </label>
          <select
            id="country_code"
            v-model="countryCode"
            class="block w-full rounded-lg border px-3 py-2 text-sm text-white bg-slate-900 focus:outline-none focus:ring-1 transition-colors"
            :class="formErrors['country_code'] ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-slate-600 focus:border-emerald-500 focus:ring-emerald-500'"
          >
            <optgroup label="—">
              <option v-for="country in FAVORITE_COUNTRIES" :key="'fav-' + country.code" :value="country.code">
                {{ country.name }}
              </option>
            </optgroup>
            <optgroup label="—">
              <option v-for="country in ALL_COUNTRIES" :key="country.code" :value="country.code">
                {{ country.name }}
              </option>
            </optgroup>
          </select>
          <p v-if="formErrors['country_code']" class="mt-1 text-xs text-red-400">
            {{ formErrors['country_code'][0] }}
          </p>
        </div>

        <!-- VAT number -->
        <div>
          <label for="vat_number" class="block text-xs font-medium text-slate-400 mb-1">
            {{ t('organizer.organisation.vat_number') }}
            <span class="text-slate-600 font-normal ml-1">({{ t('common.optional') }})</span>
          </label>
          <input
            id="vat_number"
            v-model="vatNumber"
            type="text"
            class="block w-full rounded-lg border border-slate-600 px-3 py-2 text-sm text-white bg-slate-900 focus:outline-none focus:ring-1 focus:border-emerald-500 focus:ring-emerald-500 transition-colors"
          />
        </div>

        <!-- Contact email -->
        <div>
          <label for="org_email" class="block text-xs font-medium text-slate-400 mb-1">
            {{ t('organizer.organisation.email') }}
            <span class="text-slate-600 font-normal ml-1">({{ t('common.optional') }})</span>
          </label>
          <input
            id="org_email"
            v-model="orgEmail"
            type="email"
            autocomplete="email"
            class="block w-full rounded-lg border px-3 py-2 text-sm text-white bg-slate-900 focus:outline-none focus:ring-1 transition-colors"
            :class="formErrors['email'] ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-slate-600 focus:border-emerald-500 focus:ring-emerald-500'"
          />
          <p v-if="formErrors['email']" class="mt-1 text-xs text-red-400">
            {{ formErrors['email'][0] }}
          </p>
        </div>

        <!-- Contact phone -->
        <div>
          <label for="org_phone" class="block text-xs font-medium text-slate-400 mb-1">
            {{ t('organizer.organisation.phone') }}
            <span class="text-slate-600 font-normal ml-1">({{ t('common.optional') }})</span>
          </label>
          <input
            id="org_phone"
            v-model="orgPhone"
            type="tel"
            autocomplete="tel"
            class="block w-full rounded-lg border border-slate-600 px-3 py-2 text-sm text-white bg-slate-900 focus:outline-none focus:ring-1 focus:border-emerald-500 focus:ring-emerald-500 transition-colors"
          />
        </div>

        <!-- Website -->
        <div class="sm:col-span-2">
          <label for="website" class="block text-xs font-medium text-slate-400 mb-1">
            {{ t('organizer.organisation.website') }}
            <span class="text-slate-600 font-normal ml-1">({{ t('common.optional') }})</span>
          </label>
          <input
            id="website"
            v-model="website"
            type="url"
            autocomplete="url"
            class="block w-full rounded-lg border px-3 py-2 text-sm text-white bg-slate-900 focus:outline-none focus:ring-1 transition-colors"
            :class="formErrors['website'] ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-slate-600 focus:border-emerald-500 focus:ring-emerald-500'"
          />
          <p v-if="formErrors['website']" class="mt-1 text-xs text-red-400">
            {{ formErrors['website'][0] }}
          </p>
        </div>
      </div>

      <div class="flex gap-3">
        <button
          type="submit"
          :disabled="loading"
          class="bg-emerald-500 hover:bg-emerald-400 text-white font-semibold rounded-lg px-5 py-2.5 text-sm transition-colors disabled:opacity-50"
        >
          <span v-if="loading">{{ t('common.loading') }}</span>
          <span v-else>{{ t('organizer.organisation.save') }}</span>
        </button>
        <button
          type="button"
          class="bg-slate-700 hover:bg-slate-600 text-slate-200 font-semibold rounded-lg px-5 py-2.5 text-sm border border-slate-600 transition-colors"
          @click="cancelEditing"
        >
          {{ t('organizer.organisation.cancel') }}
        </button>
      </div>
    </form>
  </div>
</template>
